/**
 * Fallauswahl für Vertretungen (Kundenfeedback 13.09.26, I).
 * Spiegel der Backend-Logik in internal/tenants/substitution/casescope.go.
 *
 * Vertreten zwei Kolleginnen gemeinsam eine abwesende Sachbearbeiterin,
 * teilen sie sich deren Akten häufig nach Fallnummer auf: die eine übernimmt
 * die ungeraden, die andere die geraden Nummern. Daneben sind freie
 * Nummernbereiche möglich („1-50, 77, 120-“).
 *
 * Reine Funktionen ohne Vue-Abhängigkeit, damit die Regeln leicht testbar
 * bleiben und in der Fallliste wie in anderen Ansichten identisch greifen.
 */

export type CaseSelectionMode = '' | 'all' | 'odd' | 'even' | 'custom'

export interface CaseSelectionRule {
  mode: CaseSelectionMode
  /** Nur bei mode === "custom": Nummernbereiche als Freitext. */
  range: string
}

export interface NumberRange {
  from: number
  /** null = offenes Ende („120-“). */
  to: number | null
}

const YEAR_GROUP = /^(19|20)\d{2}$/
const SHORT_YEAR_SUFFIX = /[/-]\s*\d{2}\s*$/

/**
 * Ermittelt die laufende Nummer eines Aktenzeichens.
 *
 * Aktenzeichen sind Freitext („00123/26“, „2026-0147“, „K-45“). Jahresangaben
 * (vierstellig 19xx/20xx oder zweistellig am Ende nach „/“ bzw. „-“) werden
 * ignoriert, damit die Parität nicht am Jahr hängt. Bleibt keine Ziffernfolge
 * übrig oder ist kein Aktenzeichen erfasst, dient die KARS-Fall-ID als
 * Grundlage – so ist jeder Fall eindeutig einer Hälfte zugeordnet.
 */
export function extractCaseSequenceNumber (
  caseNumber: string | null | undefined,
  fallbackId: number | null | undefined,
): number | null {
  const raw = (caseNumber || '').trim()
  if (raw) {
    let groups: string[] = raw.match(/\d+/g) ?? []
    // Zweistelliges Jahr am Ende („00123/26“, „12-26“) nur abschneiden, wenn
    // davor noch eine weitere Ziffernfolge steht („K-45“ bleibt 45).
    if (groups.length > 1 && SHORT_YEAR_SUFFIX.test(raw)) {
      groups = groups.slice(0, -1)
    }
    const candidates = groups.filter(g => !YEAR_GROUP.test(g))
    const pool = candidates.length > 0 ? candidates : groups
    const first = pool[0]
    if (first !== undefined) {
      const n = Number.parseInt(first, 10)
      if (Number.isFinite(n)) {
        return n
      }
    }
  }
  if (fallbackId != null && Number.isFinite(Number(fallbackId))) {
    return Number(fallbackId)
  }
  return null
}

/**
 * Parst Nummernbereiche aus Freitext: „1-50, 77, 120-“ → [1..50], [77], [120..∞].
 * Ungültige Teile werden ignoriert, statt den ganzen Filter zu kippen.
 */
export function parseNumberRanges (text: string | null | undefined): NumberRange[] {
  if (!text) {
    return []
  }
  return text
    .split(/[,;\s]+/)
    .map(part => part.trim())
    .filter(Boolean)
    .map((part): NumberRange | null => {
      const m = part.match(/^(\d+)\s*(?:-\s*(\d+)?)?$/)
      if (!m) {
        return null
      }
      const from = Number.parseInt(m[1] ?? '', 10)
      if (!Number.isFinite(from)) {
        return null
      }
      if (m[2] !== undefined) {
        const to = Number.parseInt(m[2], 10)
        return { from: Math.min(from, to), to: Math.max(from, to) }
      }
      // „120-“ = offenes Ende, „77“ = Einzelwert.
      return part.includes('-') ? { from, to: null } : { from, to: from }
    })
    .filter((r): r is NumberRange => r !== null)
}

export function matchesNumberRanges (n: number, ranges: NumberRange[]): boolean {
  return ranges.some(r => n >= r.from && (r.to === null || n <= r.to))
}

/** Prüft eine laufende Nummer gegen die Auswahlregel. */
export function matchesCaseSelection (n: number | null, rule: CaseSelectionRule): boolean {
  if (!rule.mode || rule.mode === 'all') {
    return true
  }
  if (n === null) {
    return false
  }
  switch (rule.mode) {
    case 'odd': {
      return Math.abs(n) % 2 === 1
    }
    case 'even': {
      return Math.abs(n) % 2 === 0
    }
    case 'custom': {
      const ranges = parseNumberRanges(rule.range)
      // Leere/ungültige Regel: nichts ausblenden, statt die Liste zu leeren.
      if (ranges.length === 0) {
        return true
      }
      return matchesNumberRanges(n, ranges)
    }
    default: {
      return true
    }
  }
}

/** Lesbare Kurzbeschreibung der Regel für Chips und Banner. */
export function describeCaseSelection (rule: CaseSelectionRule): string {
  switch (rule.mode) {
    case 'odd': {
      return 'nur ungerade Fallnummern'
    }
    case 'even': {
      return 'nur gerade Fallnummern'
    }
    case 'custom': {
      return rule.range?.trim() ? `Fallnummern ${rule.range.trim()}` : 'eigene Nummernauswahl'
    }
    default: {
      return 'alle Fallakten'
    }
  }
}
